// EL JARTI INGLÉS — "Mis pedidos" y seguimiento, con la estética de Amazon.
//
// Cada pedido es una tarjeta con la franja gris de cabecera (PEDIDO REALIZADO /
// TOTAL / ENVIAR A / Nº DE PEDIDO) y, al desplegar, la barra de progreso de 4
// pasos con la cuenta atrás real hasta que esté listo.
//
// La consulta FILTRA POR uid a la fuerza: las reglas de Firestore evalúan el
// `list` documento a documento y basta uno ajeno para tumbar la consulta entera.

import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { collection, query, where } from 'firebase/firestore';
import { db } from '@/firebase/config';
import { useCollection } from '@/firebase/firestore/use-collection';
import { sanitizeFirestoreData } from '@/lib/utils';
import { useAuth } from '@/hooks/useAuth';
import type { JartiOrder, JartiOrderStatus } from '@/lib/types';
import { jartiShop_cancelOrder } from '@/lib/actions';
import {
  AMZ, AMZ_FONT, AmzPillButton, JartiInglesLogo, Price, SubNav, formatEntrega, formatFecha, toMs,
} from '@/components/jartiingles/ui';
import {
  Package, Loader2, CheckCircle2, Clock, PackageCheck, XCircle, ChevronDown, ChevronUp,
  Search, ArrowLeft, Copy, Check,
} from 'lucide-react';

const PASOS: { key: JartiOrderStatus | 'pedido'; label: string }[] = [
  { key: 'pedido', label: 'Pedido realizado' },
  { key: 'preparando', label: 'Preparando' },
  { key: 'listo', label: 'Listo para recoger' },
  { key: 'entregado', label: 'Entregado' },
];

export default function JartiInglesPedidosPage() {
  const { user } = useAuth();
  const [tab, setTab] = useState<'activos' | 'todos'>('activos');
  const [busca, setBusca] = useState('');

  const ordersQuery = useMemo(
    () => (user ? query(collection(db, 'jarti_orders'), where('uid', '==', user.uid)) : null),
    [user],
  );
  const { data: rawOrders, isLoading } = useCollection<JartiOrder>(ordersQuery);

  const orders = useMemo(() => {
    const list = (rawOrders || []).map((o) => sanitizeFirestoreData(o) as JartiOrder);
    // El orden se hace en memoria: ordenar en la query junto al filtro por uid
    // exigiría un índice compuesto.
    return list.sort((a, b) => toMs(b.createdAt) - toMs(a.createdAt));
  }, [rawOrders]);

  const visibles = useMemo(() => {
    const q = busca.trim().toLowerCase();
    return orders.filter((o) => {
      if (tab === 'activos' && (o.status === 'entregado' || o.status === 'cancelado')) return false;
      if (!q) return true;
      const enLineas = (o.lines || []).some((l) => l.name.toLowerCase().includes(q));
      return o.code?.toLowerCase().includes(q) || enLineas;
    });
  }, [orders, tab, busca]);

  return (
    <div className="-m-4 min-h-screen md:-m-6 lg:-m-8" style={{ background: AMZ.page, color: AMZ.text, fontFamily: AMZ_FONT }}>
      <header style={{ background: AMZ.header }} className="px-3 py-2">
        <div className="mx-auto flex max-w-[1500px] items-center gap-4">
          <Link to="/jartinet/eljartiingles" className="rounded border border-transparent p-1 hover:border-white/60">
            <JartiInglesLogo />
          </Link>
          <div className="flex min-w-0 flex-1 items-stretch overflow-hidden rounded-[6px]">
            <input
              value={busca}
              onChange={(e) => setBusca(e.target.value)}
              placeholder="Buscar en mis pedidos"
              className="min-w-0 flex-1 bg-white px-3 py-2 text-[14px] text-[#0f1111] outline-none placeholder:text-[#767676]"
            />
            <span className="grid w-11 shrink-0 place-items-center" style={{ background: AMZ.orange }}>
              <Search className="h-5 w-5 text-[#0f1111]" />
            </span>
          </div>
          <Link to="/jartinet/eljartiingles" className="hidden items-center gap-1 rounded border border-transparent px-2 py-1 text-[13px] font-bold text-white hover:border-white/60 md:flex">
            <ArrowLeft className="h-4 w-4" /> Volver a la tienda
          </Link>
        </div>
      </header>

      <SubNav>
        <Link to="/jartinet/eljartiingles" className="whitespace-nowrap rounded border border-transparent px-2 py-0.5 hover:border-white/40">Tienda</Link>
        <span className="whitespace-nowrap rounded border border-white/70 px-2 py-0.5">Mis pedidos</span>
      </SubNav>

      <main className="mx-auto max-w-[1100px] px-3 py-5">
        <h1 className="text-[28px] font-normal">Mis pedidos</h1>

        <div className="mt-3 flex gap-5 border-b text-[14px]" style={{ borderColor: AMZ.border }}>
          {([['activos', 'En curso'], ['todos', 'Todos los pedidos']] as const).map(([k, label]) => (
            <button
              key={k}
              onClick={() => setTab(k)}
              className="relative pb-2 font-bold"
              style={{ color: tab === k ? AMZ.text : AMZ.link }}
            >
              {label}
              {tab === k && <span className="absolute inset-x-0 -bottom-px h-[3px] rounded-t" style={{ background: AMZ.orange }} />}
            </button>
          ))}
        </div>

        {isLoading ? (
          <div className="grid place-items-center py-20"><Loader2 className="h-8 w-8 animate-spin" style={{ color: AMZ.muted }} /></div>
        ) : visibles.length === 0 ? (
          <div className="mt-4 rounded-[4px] bg-white p-10 text-center">
            <Package className="mx-auto mb-3 h-12 w-12" style={{ color: '#c7c9c9' }} />
            <p className="text-[17px] font-bold">
              {tab === 'activos' ? 'No tienes pedidos en curso' : 'Todavía no has hecho ningún pedido'}
            </p>
            <Link to="/jartinet/eljartiingles">
              <AmzPillButton className="mt-4">Ir a la tienda</AmzPillButton>
            </Link>
          </div>
        ) : (
          <div className="mt-4 space-y-4">
            {visibles.map((o) => <TarjetaPedido key={o.id} order={o} />)}
          </div>
        )}
      </main>
    </div>
  );
}

function TarjetaPedido({ order }: { order: JartiOrder }) {
  const [abierto, setAbierto] = useState(false);
  const [cancelando, setCancelando] = useState(false);
  const [copiado, setCopiado] = useState(false);
  const [, forceTick] = useState(0);

  const readyMs = toMs(order.readyAt);
  const enCurso = order.status === 'preparando';

  // Reloj vivo solo mientras el pedido se prepara.
  useEffect(() => {
    if (!enCurso) return;
    const id = setInterval(() => forceTick((n) => n + 1), 1000);
    return () => clearInterval(id);
  }, [enCurso]);

  const cancelar = async () => {
    if (!window.confirm(`¿Cancelar el pedido ${order.code}? Se te devolverá el importe completo.`)) return;
    setCancelando(true);
    const r = await jartiShop_cancelOrder(order.id);
    setCancelando(false);
    if (!r?.success) window.alert(r?.message || 'No se pudo cancelar el pedido.');
  };

  const copiarComando = async () => {
    try {
      await navigator.clipboard.writeText('/eljartiingles paquete');
      setCopiado(true);
      setTimeout(() => setCopiado(false), 1500);
    } catch { /* sin portapapeles: da igual */ }
  };

  const titular = (() => {
    if (order.status === 'entregado') return { text: 'Entregado', color: AMZ.stock, icon: CheckCircle2 };
    if (order.status === 'cancelado') return { text: 'Pedido cancelado', color: AMZ.muted, icon: XCircle };
    if (order.status === 'listo') return { text: 'Listo para recoger en el juego', color: AMZ.stock, icon: PackageCheck };
    return { text: `Llega ${formatEntrega(readyMs)}`, color: AMZ.text, icon: Clock };
  })();
  const Icono = titular.icon;

  return (
    <article className="overflow-hidden rounded-[8px] border bg-white" style={{ borderColor: AMZ.border }}>
      {/* Franja de cabecera */}
      <div className="flex flex-wrap items-center gap-6 border-b bg-[#f0f2f2] px-5 py-3 text-[12px]" style={{ borderColor: AMZ.border }}>
        <div>
          <p className="uppercase" style={{ color: AMZ.muted }}>Pedido realizado</p>
          <p className="text-[13px]">{formatFecha(order.createdAt)}</p>
        </div>
        <div>
          <p className="uppercase" style={{ color: AMZ.muted }}>Total</p>
          <Price value={order.total} size="sm" />
        </div>
        <div>
          <p className="uppercase" style={{ color: AMZ.muted }}>Enviar a</p>
          <p className="text-[13px] font-bold">{order.mcName || 'Tu personaje'}</p>
        </div>
        <div className="ml-auto text-right">
          <p className="uppercase" style={{ color: AMZ.muted }}>Pedido nº {order.code}</p>
          <button onClick={() => setAbierto((v) => !v)} className="inline-flex items-center gap-1 text-[13px] hover:underline" style={{ color: AMZ.link }}>
            {abierto ? 'Ocultar detalles' : 'Ver detalles'}
            {abierto ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
          </button>
        </div>
      </div>

      {/* Cuerpo */}
      <div className="grid grid-cols-1 gap-4 p-5 md:grid-cols-[minmax(0,1fr)_220px]">
        <div>
          <p className="flex items-center gap-2 text-[18px] font-bold" style={{ color: titular.color }}>
            <Icono className="h-5 w-5" /> {titular.text}
          </p>
          {order.status === 'listo' && (
            <p className="mt-1 text-[13px]" style={{ color: AMZ.muted }}>
              Entra al servidor principal y escribe <span className="rounded bg-[#232f3e] px-1.5 py-0.5 font-mono text-white">/eljartiingles paquete</span>
            </p>
          )}
          {enCurso && <CuentaAtras createdMs={toMs(order.createdAt)} readyMs={readyMs} />}

          <div className="mt-4 space-y-3">
            {(order.lines || []).map((l, i) => (
              <div key={i} className="flex items-center gap-3">
                <div className="grid h-16 w-16 shrink-0 place-items-center rounded-[4px] bg-[#f7f8f8]">
                  {l.imageUrl
                    ? <img src={l.imageUrl} alt="" className="max-h-16 max-w-full object-contain" />
                    : <Package className="h-7 w-7" style={{ color: '#c7c9c9' }} />}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-[14px]">{l.name}</p>
                  <p className="text-[12px]" style={{ color: AMZ.muted }}>Cantidad: {l.qty}</p>
                </div>
                <Price value={l.unitPrice * l.qty} size="sm" />
              </div>
            ))}
          </div>
        </div>

        <div className="space-y-2">
          {order.status === 'listo' && (
            <AmzPillButton block onClick={copiarComando}>
              {copiado ? <span className="inline-flex items-center gap-1"><Check className="h-3.5 w-3.5" /> Copiado</span>
                : <span className="inline-flex items-center gap-1"><Copy className="h-3.5 w-3.5" /> Copiar comando</span>}
            </AmzPillButton>
          )}
          <AmzPillButton block variant="ghost" onClick={() => setAbierto((v) => !v)}>Seguir pedido</AmzPillButton>
          {enCurso && (
            <AmzPillButton block variant="ghost" disabled={cancelando} onClick={cancelar}>
              {cancelando ? 'Cancelando…' : 'Cancelar pedido'}
            </AmzPillButton>
          )}
        </div>
      </div>

      {/* Detalle desplegable */}
      {abierto && (
        <div className="border-t px-5 py-5" style={{ borderColor: AMZ.border, background: '#fbfbfb' }}>
          <BarraProgreso status={order.status} />

          <div className="mt-5 grid grid-cols-1 gap-5 md:grid-cols-2">
            <div>
              <h3 className="mb-2 text-[15px] font-bold">Historial</h3>
              <ol className="space-y-2">
                {(order.tracking || []).map((t, i) => (
                  <li key={i} className="flex gap-3 text-[13px]">
                    <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full" style={{ background: AMZ.stock }} />
                    <span>
                      <span className="block font-bold">{t.note || t.status}</span>
                      <span className="block" style={{ color: AMZ.muted }}>{formatFechaHora(t.at)}</span>
                    </span>
                  </li>
                ))}
                {(order.tracking || []).length === 0 && <li className="text-[13px]" style={{ color: AMZ.muted }}>Sin eventos.</li>}
              </ol>
            </div>

            <div>
              <h3 className="mb-2 text-[15px] font-bold">Contenido del paquete</h3>
              <ul className="space-y-1">
                {(order.lines || []).flatMap((l) => (l.items || []).map((it, j) => (
                  <li key={`${l.productId}-${j}`} className="flex items-center gap-2 text-[13px]">
                    <span className="font-bold">{it.count * l.qty}×</span>
                    <span className="font-mono text-[12px]" style={{ color: AMZ.muted }}>{it.id}</span>
                  </li>
                )))}
              </ul>
              <h3 className="mb-2 mt-4 text-[15px] font-bold">Pago</h3>
              <p className="text-[13px]" style={{ color: AMZ.muted }}>
                Cargado en <span className="font-mono">{order.accountIban || '—'}</span>
                {order.refunded ? ' · Reembolsado' : ''}
              </p>
            </div>
          </div>
        </div>
      )}
    </article>
  );
}

function CuentaAtras({ createdMs, readyMs }: { createdMs: number; readyMs: number }) {
  const restante = Math.max(0, readyMs - Date.now());
  const h = Math.floor(restante / 3600000);
  const m = Math.floor((restante % 3600000) / 60000);
  const s = Math.floor((restante % 60000) / 1000);
  // Progreso = cuánto ha avanzado la preparación respecto a su duración total.
  const total = Math.max(1, readyMs - createdMs);
  const pct = restante <= 0 ? 100 : Math.max(3, Math.min(97, ((total - restante) / total) * 100));

  return (
    <div className="mt-2 max-w-md">
      <p className="text-[13px]" style={{ color: AMZ.muted }}>
        {restante <= 0 ? 'Preparando los últimos detalles…' : `Listo en ${h > 0 ? `${h} h ` : ''}${m} min ${String(s).padStart(2, '0')} s`}
      </p>
      <div className="mt-1 h-1.5 overflow-hidden rounded-full" style={{ background: '#e3e6e6' }}>
        <div className="h-full rounded-full transition-[width] duration-1000" style={{ width: `${pct}%`, background: AMZ.orange }} />
      </div>
    </div>
  );
}

function BarraProgreso({ status }: { status: JartiOrderStatus }) {
  if (status === 'cancelado') {
    return (
      <div className="flex items-center gap-2 text-[14px]" style={{ color: AMZ.muted }}>
        <XCircle className="h-5 w-5" /> Este pedido se canceló y el importe se devolvió a tu cuenta.
      </div>
    );
  }
  const idx = status === 'entregado' ? 3 : status === 'listo' ? 2 : 1;

  return (
    <div className="flex items-start">
      {PASOS.map((p, i) => {
        const hecho = i <= idx;
        return (
          <div key={p.key} className="flex flex-1 items-start">
            <div className="flex flex-1 flex-col items-center text-center">
              <span
                className="grid h-7 w-7 place-items-center rounded-full text-[12px] font-bold"
                style={{ background: hecho ? AMZ.stock : '#e3e6e6', color: hecho ? '#fff' : AMZ.muted }}
              >
                {hecho ? <Check className="h-4 w-4" /> : i + 1}
              </span>
              <span className="mt-1 max-w-[110px] text-[12px]" style={{ color: hecho ? AMZ.text : AMZ.muted }}>{p.label}</span>
            </div>
            {i < PASOS.length - 1 && (
              <span className="mt-3.5 h-[3px] flex-1 rounded" style={{ background: i < idx ? AMZ.stock : '#e3e6e6' }} />
            )}
          </div>
        );
      })}
    </div>
  );
}

function formatFechaHora(v: any) {
  const ms = toMs(v);
  if (!ms) return '—';
  return new Date(ms).toLocaleString('es-ES', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
}
